import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as Sentry from "@sentry/browser";
import { graphql, useMutation } from "react-relay";

import { usePolling } from "app/hooks/relay/usePolling";
import { ExportConnectionMutation } from "app/mutations";
import { downloadFile } from "app/utils/downloadFile";

import type { useCsvExportProgressQuery } from "./__generated__/useCsvExportProgressQuery.graphql";
import type {
  ExportConnectionInput,
  ExportConnectionMutation as ExportConnectionMutationType,
} from "app/mutations/__generated__/ExportConnectionMutation.graphql";

const csvExportQuery = graphql`
  query useCsvExportProgressQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on ConnectionExport {
        completed
        downloadURL
        message
        processed
        success
      }
    }
  }
`;

interface UseCsvExportProgressOptions {
  exportInProgress: boolean;
  nodeId?: string | null;
  field: string;
  columns: readonly { name: string; path: string; subpath?: string }[];
  rowCount: number;
  params?: ExportConnectionInput["params"];
  extraPaths?: ExportConnectionInput["extraPaths"];
  onComplete?: () => void;
  onError?: (message: string) => void;
}

export function useCsvExportProgress({
  exportInProgress,
  nodeId,
  field,
  columns,
  rowCount,
  params,
  extraPaths,
  onComplete,
  onError,
}: UseCsvExportProgressOptions) {
  const [exportId, setExportId] = useState("");
  const [percentage, setPercentage] = useState(0);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const [startExport] = useMutation<ExportConnectionMutationType>(
    ExportConnectionMutation,
  );

  useEffect(() => {
    return () => clearTimeout(completionTimerRef.current);
  }, []);

  useEffect(() => {
    if (!exportInProgress) return;

    const disposable = startExport({
      variables: {
        input: {
          node: nodeId,
          field,
          columns,
          format: "CSV",
          params,
          extraPaths,
        },
      },
      onCompleted(res, err) {
        if (err) Sentry.captureException(err);
        if (res.exportConnection?.export?.id) {
          setExportId(res.exportConnection.export.id);
        }
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });

    return () => disposable.dispose();
  }, [
    columns,
    startExport,
    field,
    nodeId,
    params,
    extraPaths,
    exportInProgress,
  ]);

  const handlePollError = useCallback(
    (error: Error) => {
      onError?.(error.message);
      setPercentage(0);
      setExportId("");
      onComplete?.();
    },
    [onComplete, onError],
  );

  const handlePollData = useCallback(
    (data: useCsvExportProgressQuery["response"]): true | undefined => {
      const node = data.node;
      if (node?.__typename !== "ConnectionExport") return;

      if (node.success === false) {
        Sentry.captureException(
          new Error(`CSV export failed: ${node.message}`),
        );
        onError?.(node.message ?? "Export failed");
        setPercentage(0);
        setExportId("");
        onComplete?.();
        return true;
      }

      if (node.completed && node.downloadURL) {
        setPercentage(100);
        downloadFile(
          node.downloadURL,
          node.downloadURL.split("/").pop() || "",
          "download",
        );
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = setTimeout(() => {
          setPercentage(0);
          setExportId("");
          onComplete?.();
        }, 500);
        return true;
      }

      if (node.processed != null) {
        setPercentage(Math.max(5, (node.processed / rowCount) * 100));
      }
    },
    [onComplete, onError, rowCount],
  );

  const variables = useMemo(() => ({ id: exportId }), [exportId]);

  usePolling<useCsvExportProgressQuery>({
    query: csvExportQuery,
    variables,
    interval: 500,
    enabled: exportInProgress && Boolean(exportId),
    onData: handlePollData,
    onError: handlePollError,
  });

  return {
    showProgress: exportInProgress && Boolean(exportId),
    percentage,
  };
}
